import { format } from 'date-fns'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getStatus } from '../../../../../hooks/status'
import { useContextState } from '../../../../../hooks/theme'
import { Constants } from '../../../../../utils/constants'
import { userScheduleServiceFetch } from '../../../../../utils/constants/callService'
import { getPreviousYears, monthYear } from '../../../../../utils/constants/date_month'
import { Roles, Status } from '../../../../../utils/enums'
import {
  getArea,
  getDepartments,
  getDivisions,
  getQuestionnaires,
  getRoles,
  getUserTypes,
  getUsers,
} from '../../../../../utils/methods/geServiceWithItemsFormat'
import { Item } from '../../../../../utils/types/generals'
import { canAccess } from '../../../../../utils/validate/canaccess'
import { FrecuencyTypeTextResponse } from '../../../../calendar/pages/main/enums/enums'
import { FrequencyEnumTable, SerieEnumTextUper } from '../../../../calendar/pages/form/components/steps/configCalendar/enums'

const useGetData = () => {
  const { colors } = useContextState()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [items, setItems] = useState<{ [x: string]: Item[] }>({})
  const [departments, setDepartments] = useState<any[]>([])
  const [endpointDetail, setEndpointDetail] = useState({ total: 0 })
  const [row, setRow] = useState<
    {
      id: number
      division: string
      user: string
      questionnaire: string
      reportUrl: string
      available_to: string
      available_from: string
      status: {
        value: Status
        color: string
        text: string
      }
    }[]
  >([])

  const handleItemsByExternal = ({
    name,
    value,
  }: {
    name: string
    value: any
  }) => {
    setItems((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const getFrecuencyName = (type: FrecuencyTypeTextResponse) => {
    let text = '';
    switch (type) {
      case FrecuencyTypeTextResponse.CYCLICAL:
        text = SerieEnumTextUper.CYCLIC;
        break
      case FrecuencyTypeTextResponse.DAILY:
        text = SerieEnumTextUper.DAYLI;
        break
      case FrecuencyTypeTextResponse.WEEKLY:
        text = SerieEnumTextUper.WEEKLY;
        break
      case FrecuencyTypeTextResponse.UNIQUE:
        text = SerieEnumTextUper.UNIQUE;
        break
      default:
        text = ''
    }
    return text;
  }

  const getFrecuencyValue = (type: FrecuencyTypeTextResponse) => {
    let text: FrequencyEnumTable;
    switch (type) {
      case FrecuencyTypeTextResponse.CYCLICAL:
        text = FrequencyEnumTable.CYCLIC;
        break
      case FrecuencyTypeTextResponse.DAILY:
        text = FrequencyEnumTable.DAYLI;
        break
      case FrecuencyTypeTextResponse.WEEKLY:
        text = FrequencyEnumTable.WEEKLY;
        break
      case FrecuencyTypeTextResponse.UNIQUE:
        text = FrequencyEnumTable.UNIQUE;
        break
    }
    return text;
  }

  const normalizeHex = (hex?: string | null) =>
    hex ? `#${String(hex).replace('#', '')}` : undefined


  const getUserSchedule = async (payload?: { query: string }) => {
    setIsLoading(true)
    const response = await userScheduleServiceFetch.getList(payload)
    let newRow: {
      id: number
      division: string
      user: string
      questionnaire: string
      available_to: string
      available_from: string
      reportUrl: string
      status: {
        value: Status
        color: string
        text: string
      }
    }[] = []

    if (!response?.errors) {
      setEndpointDetail({
        total: response.totalPages,
      })

      newRow = response?.items?.map((item) => {
        const isScheduleOpen = Boolean(Number(item?.openScheduled ?? 0))
        const storeStatusText = item?.storeStatusName ?? null
        const storeStatusColor = normalizeHex(item?.storeStatusColor ?? null)
        return {
          id: item.id,
          store: `${item.storeKeyCode ?? ''}${item.store ? ' - ' + item.store : ''}`,
          user: item?.userAssignFullname,
          reportUrl: item?.reportUrl,
          division: item.division ?? '',
          frequencyType: !!item.frequencyType ? getFrecuencyName(item.frequencyType as FrecuencyTypeTextResponse) : 'N/A',
          frequencyTypeValue: !!item.frequencyType ? getFrecuencyValue(item.frequencyType as FrecuencyTypeTextResponse) : FrequencyEnumTable.FRRE,
          createById: item?.createdBy,
          serieId: item?.sequenceId,
          scheduleType: t(`schedule.scheduleTypes.${isScheduleOpen ? 'free' : 'programmed'}`),
          available_to: item?.dateEnd
            ? format(
              new Date(item?.dateEnd).getTime(),
              Constants.dateFormatGeneral
            )
            : '',
          available_from: item?.dateStart
            ? format(
              new Date(item?.dateStart).getTime(),
              Constants.dateFormatGeneral
            )
            : '',
          questionnaire: item.questionnaireName ?? '',
          status: getStatus(item.statusId, colors, t),
          storeStatusInline:
            storeStatusText && storeStatusColor
              ? { text: storeStatusText, color: storeStatusColor }
              : null,
        }
      })
    }

    setRow(newRow)
    setIsLoading(false)
  }

  const getRolesService = async () => {
    const responseRoles = await getRoles({ skip: [Roles.ADMIN] })

    setItems((prev) => ({
      ...prev,
      roles: responseRoles.items,
    }))
  }

  const getAreasService = async () => {
    const responseAreas = await getArea()

    setItems((prev) => ({
      ...prev,
      areas: responseAreas,
    }))
  }

  const getDepartmentService = async () => {
    const departments = await getDepartments()
    setDepartments(departments)
  }

  const getQuestionnairesService = async () => {
    const responseQuestionarie = await getQuestionnaires()

    setItems((prev) => ({
      ...prev,
      questionnaireId: responseQuestionarie.items,
    }))
  }

  const getUserTypesService = async () => {
    const { items } = await getUserTypes()

    setItems((prev) => ({
      ...prev,
      userTypeId: items,
    }))
  }

  const getUsersService = async () => {
    const { items } = await getUsers({ addUserLogged: true })

    setItems((prev) => ({
      ...prev,
      userId: items,
    }))
  }

  const getDivisionsService = async () => {
    const division = await getDivisions()

    setItems((prev) => ({
      ...prev,
      divisions: division.items,
    }))
  }

  const getItemStatus = async () => {
    const statusList = [
      Status.TO_BE_STARTED,
      Status.STARTED,
      Status.PAUSE,
      Status.EXPIRED,
      Status.INCOMPLETE,
      Status.FINISHED,
      Status.CANCEL,
      Status.CLOSE,
      Status.WAITING
    ]

    setItems((prev) => ({
      ...prev,
      status: statusList.map((item) => {
        const status = getStatus(item, colors, t)
        return {
          label: status.text,
          value: status.value,
        }
      }),
    }))
  }

  const getDates = () => {
    setItems((prev) => ({
      ...prev,
      createYear: getPreviousYears(),
      month: monthYear.map(item => ({
        ...item,
        value: item.value + 1
      })),
    }))
  }

  const getAllData = useCallback(() => {
    getRolesService()
    getUserTypesService()
    getUsersService()
    getDivisionsService()
    getDates()
    getAreasService()
    getQuestionnairesService()
    getItemStatus()
    getDepartmentService()
  }, [])

  return {
    row,
    isLoading,
    items,
    departments,
    endpointDetail,
    setRow,
    handleItemsByExternal,
    getUserSchedule,
    getAllData
  }
}

export { useGetData }
